/**
 * How often a playing host republishes sync/player. Shared with the heartbeat
 * itself in services/sessionSync.js so the staleness window below cannot drift
 * away from the interval it is derived from.
 */
export const PLAYER_HEARTBEAT_MS = 8000;

/**
 * How old a `playing` checkpoint may be before it stops counting as proof that
 * a host player instance is still running.
 *
 * Nothing publishes a final pause when a host tab closes, so the age of
 * `updatedAtMs` is the only evidence available. Three heartbeat intervals
 * absorb one dropped beat plus network jitter without declaring a live host
 * stale, while still resolving an abandoned party within half a minute.
 */
export const PLAYER_STALE_AFTER_MS = PLAYER_HEARTBEAT_MS * 3;

/**
 * True when a checkpoint is too old to be backed by a live host, and for a
 * missing or malformed timestamp, which must never be read as "playing now".
 */
export function isPlaybackCheckpointStale(
  updatedAtMs,
  nowMs = Date.now(),
  staleAfterMs = PLAYER_STALE_AFTER_MS,
) {
  const updated = Number(updatedAtMs);
  const now = Number(nowMs);
  if (!Number.isFinite(updated) || updated <= 0 || !Number.isFinite(now)) return true;
  return now - updated > staleAfterMs;
}

/**
 * Shared playback only counts as active while a recent heartbeat backs it, so a
 * guest never shows a track running for a host who has gone away.
 */
export function isRemotePlaybackActive({ isPlaying, updatedAtMs } = {}, nowMs = Date.now()) {
  return isPlaying === true && !isPlaybackCheckpointStale(updatedAtMs, nowMs);
}

/** YouTube IFrame API player states, numeric so this never depends on window.YT. */
export const YOUTUBE_PLAYER_STATE = {
  unstarted: -1,
  ended: 0,
  playing: 1,
  paused: 2,
  buffering: 3,
  cued: 5,
};

/**
 * Whether a YouTube state means audio is actually running.
 *
 * Only PLAYING is playback. UNSTARTED, CUED and PAUSED are all idle, and
 * mapping them is what stops the transport from reporting playback while the
 * iframe sits idle — the state a blocked autoplay leaves behind. BUFFERING
 * returns null so a mid-track stall does not flip the transport to paused.
 */
export function resolveYouTubePlayingState(state) {
  const value = Number(state);
  if (value === YOUTUBE_PLAYER_STATE.playing) return true;
  if (
    value === YOUTUBE_PLAYER_STATE.unstarted
    || value === YOUTUBE_PLAYER_STATE.cued
    || value === YOUTUBE_PLAYER_STATE.paused
  ) {
    return false;
  }
  return null;
}

/** Interpolated playback position for guest display and host recovery. Host YouTube is the live clock. */
export function expectedTime(positionSeconds, isPlaying, updatedAtMs, nowMs = Date.now()) {
  const position = Number(positionSeconds);
  const safePosition = Number.isFinite(position) && position > 0 ? position : 0;
  if (!isPlaying) return safePosition;

  const updated = Number(updatedAtMs);
  const now = Number(nowMs);
  if (!Number.isFinite(updated) || !Number.isFinite(now) || now <= updated) {
    return safePosition;
  }

  return safePosition + (now - updated) / 1000;
}

export function shouldAdvanceOnEnded(isHost) {
  return isHost === true;
}

export function shouldMountYouTubePlayer(isHost) {
  return isHost === true;
}

export function parseDurationSeconds(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) return 0;

  const iso = value.trim().match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (iso) {
    return (Number(iso[1]) || 0) * 3600 + (Number(iso[2]) || 0) * 60 + (Number(iso[3]) || 0);
  }

  const parts = value.trim().split(':').map((part) => Number(part));
  if (parts.some((part) => !Number.isFinite(part) || part < 0)) return 0;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

export function shouldRunHeartbeat({ isHost, isPlaying }) {
  return isHost === true && isPlaying === true;
}

/**
 * Position to restore when a host re-acquires a party.
 *
 * A stored `isPlaying: true` only proves the previous host tab was playing when
 * it last wrote, not that audio is running now, so elapsed wall-clock time is
 * added only while a recent heartbeat still backs the checkpoint. A checkpoint
 * abandoned by a closed tab restores at its stored position rather than being
 * advanced by the entire absence, and the result never passes a known duration,
 * so a recovering host cannot seek past the end of the track.
 */
export function recoverHostPosition(
  positionSeconds,
  isPlaying,
  updatedAtMs,
  nowMs = Date.now(),
  durationSeconds = 0,
) {
  const position = Number(positionSeconds);
  const stored = Number.isFinite(position) && position > 0 ? position : 0;
  const recovered = isRemotePlaybackActive({ isPlaying, updatedAtMs }, nowMs)
    ? expectedTime(stored, true, updatedAtMs, nowMs)
    : stored;

  const duration = Number(durationSeconds);
  if (Number.isFinite(duration) && duration > 0) {
    return Math.min(recovered, duration);
  }
  return recovered;
}

export function hasPlayerCheckpoint(data) {
  return Boolean(data?.song && typeof data.song === 'object' && data.song.id);
}

export function canAutostartQueueHead({ isHost, recoveryStatus, currentSong, queueLength }) {
  return isHost === true
    && recoveryStatus === 'no-checkpoint'
    && !currentSong
    && Number(queueLength) > 0;
}

export function canStartPlayerHeartbeat({ isHost, isPlaying, recoveryStatus }) {
  if (recoveryStatus === 'pending') return false;
  return shouldRunHeartbeat({ isHost, isPlaying });
}

export function applyHostYouTubeReady(player, { currentTime, isPlaying } = {}) {
  const recovered = Math.max(0, Number(currentTime) || 0);
  if (recovered > 0 && typeof player?.seekTo === 'function') {
    player.seekTo(recovered, true);
  }
  if (isPlaying) {
    player?.playVideo?.();
    return 'play';
  }
  player?.pauseVideo?.();
  return 'pause';
}

export function shouldApplyPlayerVersion(remoteVersion, lastAppliedVersion, lastWrittenVersion) {
  const remote = Number(remoteVersion);
  if (!Number.isFinite(remote)) return false;
  return remote > Number(lastAppliedVersion ?? -1) && remote > Number(lastWrittenVersion ?? -1);
}
